<template>
    <mini-calculator-page>
        <div slot="summary-panel">
            <div class="p-animated-calc__results a-form-inputs u-full-width u-margin-auto">
                <mini-calculator-header title="Interpolated Multipliers" icon="interpolated"/>

                <control label="Ogden Table">
                    <select v-model="ogdenTable" ref="ogdenTable" @change="reCalculateMultiplier()">
                        <option :value="null" disabled hidden>
                            Please select...
                        </option>
                        <option :value="key" v-for="(item, key) in ogdenTableInfo.tables">
                            {{ key.charAt(0).toUpperCase() + key.slice(1).replace('-', ' ') }}
                            -
                            {{ item.name }}
                        </option>
                    </select>
                </control>

                <control label="Discount Rate">
                    <select v-model="discountRate" ref="discountRate" @change="reCalculateMultiplier()">
                        <option :value="null" disabled hidden>
                            Please select...
                        </option>
                        <option :value="key" v-for="(item, key) in ogdenTableInfo.columns">
                            {{ item }}%
                        </option>
                    </select>
                </control>
                <control label="Date of Birth" v-show="ogdenTable!=='table-28'">
                    <date-input @input="reCalculateMultiplier()"
                                v-field-control
                                name="date_of_birth"
                                required
                                v-model="dateOfBirth" ref="dateOfBirth"></date-input>
                    <control-error :show="$error('date_of_birth')">
                        Date of Birth is required
                    </control-error>
                </control>

                <control for="age_of_deceased" label="Date of Trial"
                         v-show="ogdenTable!=='table-28'">
                    <date-input @input="reCalculateMultiplier()"
                                :disabled="dateOfBirth===null || dateOfBirth===''"
                                v-model="dateOfTrial" ref="dateOfTrial"></date-input>
                </control>

                <control label="Age at Trial" v-show="ogdenTable!=='table-28'">
                    <input autocomplete="off" @input="reCalculateMultiplier()"
                           name="age_of_deceased"
                           :disabled="dateOfBirth===null || dateOfBirth===''"
                           v-model="ageAtTrial" ref="ageAtTrial">
                    <control-error :show="!isValidAgeAtTrial()">
                        {{ ageAtTrialValidationMessage() }}
                    </control-error>
                </control>
                <control label="Term" v-show="ogdenTable==='table-28'">
                    <input autocomplete="off" @input="reCalculateMultiplier()" name="age_of_deceased"
                           v-model="term" ref="term">
                    <control-error :show="!isValidTerm()">
                        {{ termValidationMessage() }}
                    </control-error>
                </control>

                <h2 class="u-r-margin-top-40">
                    Result
                </h2>

                <control label="Interpolated Multiplier">
                    <input autocomplete="off" disabled :value="roundValue(multiplier, 2)">
                </control>
            </div>
        </div>
        <div slot="results-panel" class="flex-grid--vertical" v-show="multiplier > 0">
            <div class="p-animated-calc__results-panel u-full-width u-margin-auto">
                <div v-if="multiplier">
                    <h2>Explanation</h2>

                    <p>
                        {{ ogdenTableInfo.tables[ogdenTable].name
                        }} are calculated using Ogden {{ ogdenTable.charAt(0).toUpperCase() + ogdenTable.slice(1).replace('-', ' ')
                        }}.
                    </p>

                    <p v-if="tableData.table.tbody.length === 1">
                        Since the person's age at trial is a round figure, {{ ageAtTrial
                        }} years old, an exact row can be found within the Ogden Table.
                        <br>
                        <br>
                        Using the discount rate of {{ discountRate
                        }}% we can determine the multiplier value to be <strong>{{ roundValue(multiplier, 2) }}</strong>
                    </p>

                    <p v-else>
                        Since the person's age at trial is not a round figure, {{ ageAtTrial
                        }} years old, an exact row of the Ogden Table is not available. Therefore, we must
                        use the nearest two rows and interpolate the figure from between them.
                        <br>
                        <br>
                        Using the discount rate of {{ discountRate
                        }}% and interpolating between the two values the multiplier value can be found to be <strong>{{ roundValue(multiplier, 2)
                        }}</strong>
                    </p>

                    <div class="a-table--really-wide" v-if="tableData !== null">
                        <table class="a-table a-table--full-width a-table--modal">
                            <thead>
                            <tr>
                                <th v-for="column in tableData.table.thead">
                                    {{ column }}
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="row in tableData.table.tbody">
                                <td v-for="column in row">
                                    {{ column }}
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>

                    <div v-if="tableData.table.tbody.length > 1">
                        <h2>Calculation</h2>
                        <p>
                            The person's age at the assumed date of trial is {{ ageAtTrial
                            }} years old which, as it is not a round figure, lies between rows {{ formulaData()[0] }}
                            and {{ formulaData()[2] }} of Ogden Table
                            {{ ogdenTable.charAt(0).toUpperCase() + ogdenTable.slice(1).replace('-', ' ')}}.
                            Therefore, interpolation between rows {{ formulaData()[0] }} and {{ formulaData()[2] }} is
                            required on the {{ discountRate }}% column to receive the projected mortality value:
                        </p>

                        <div class="m-formula">
                            <div class="m-formula__item--row">
                                {{ formulaData()[3] }} + (({{ formulaData()[2] }} - {{ ageAtTrial || term }}) x ({{ formulaData()[1] }} - {{ formulaData()[3] }})) = {{ roundValue(multiplier, 2) }}
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    </mini-calculator-page>
</template>

<script type="text/babel">
    import {mapGetters, mapActions} from 'vuex';
    import Control from 'components/form/Control.vue';
    import ControlError from 'components/form/control/Error.vue';
    import FormValidator from 'components/form/validator/mixin';
    import MiniCalculatorPage from 'pages/mini-calculators/components/MiniCalculatorPage';
    import CalculatorKeyBindings from 'pages/mini-calculators/calculators/mixins/MiniCalculatorBindings';
    import miniCalcIcons from 'components/Icons.vue';
    import Intro from 'pages/mini-calculators/calculators/CalculatorComponents/Intro';
    import DateInput from 'components/form/control/DateInput.vue';
    import roundValue from 'mixins/roundValue';
    import MiniCalculatorHeader from 'pages/mini-calculators/components/MiniCalculatorHeader';
    import moment from 'moment';
    import _ from "lodash";

    export default {
        name: 'interpolated-multipliers',
        computed: {
            ...mapGetters('mini/interpolatedMultipliers', {
                findValue: 'findValue',
            }),
            ogdenTable: {
                get() {
                    return this.findValue('ogdenTable');
                },
                set(value) {
                    this.updateValue({key: 'ogdenTable', value});
                    this.resetCheck();
                }
            },
            discountRate: {
                get() {
                    return this.findValue('discountRate');
                },
                set(value) {
                    this.updateValue({key: 'discountRate', value});
                }
            },
            dateOfBirth: {
                get() {
                    return this.findValue('dateOfBirth');
                },
                set(value) {
                    this.updateValue({key: 'dateOfBirth', value});
                    this.calculateAgeAtTrial();
                }
            },
            dateOfTrial: {
                get() {
                    return this.findValue('dateOfTrial');
                },
                set(value) {
                    this.updateValue({key: 'dateOfTrial', value});
                    this.calculateAgeAtTrial();
                }
            },
            ageAtTrial: {
                get() {
                    return this.findValue('ageAtTrial');
                },
                set(value) {
                    this.updateValue({key: 'ageAtTrial', value});
                    this.calculateDateOfTrial();
                }
            },
            term: {
                get() {
                    return this.findValue('term');
                },
                set(value) {
                    this.updateValue({key: 'term', value});
                }
            },
            ogdenTableInfo: {
                get() {
                    return this.findValue('ogdenTableInfo');
                }
            },
            multiplier: {
                get() {
                    let multiplier = this.findValue('multiplier');

                    if (multiplier > 0) {
                        this.hidePrompt();
                    }

                    return multiplier;
                }
            },
            tableData: {
                get() {
                    return this.findValue('tableData');
                }
            },
        },
        mixins: [
            FormValidator,
            roundValue,
            CalculatorKeyBindings
        ],
        components: {
            Control,
            ControlError,
            DateInput,
            MiniCalculatorPage,
            MiniCalculatorHeader,
            miniCalcIcons,
            Intro,
        },
        mounted: function () {
            this.loadOgdenTableInfo();
            this.resetValues();
        },
        methods: {
            ...mapActions('mini/interpolatedMultipliers', {
                updateValue: 'updateValue',
                loadOgdenTableInfo: 'loadOgdenTableInfo',
                calculateMultiplier: 'calculateMultiplier',
                resetValues: 'resetValues',
            }),
            isValid() {
                return (
                    this.ogdenTable !== null
                    && this.discountRate !== null
                    && (
                        this.ogdenTable !== 'table-28'
                        && this.dateOfBirth
                        && this.ageAtTrial
                        && this.dateOfTrial
                        && this.isValidAgeAtTrial()
                    )
                    || (
                        this.ogdenTable === 'table-28'
                        && this.isValidTerm()
                    )
                );
            },
            reCalculateMultiplier() {
                if (!this.isValid()) {
                    return;
                }

                this.calculateMultiplier();
            },
            calculateAgeAtTrial() {
                if (this.dateOfBirth && this.dateOfTrial) {
                    let dateOfTrial = moment(this.dateOfTrial, 'DD/MM/YYYY');
                    let dateOfBirth = moment(this.dateOfBirth, 'DD/MM/YYYY');
                    let ageAtTrial = this.roundValue(dateOfTrial.diff(dateOfBirth, 'days') / 365.25, 2);
                    this.updateValue({key: 'ageAtTrial', value: ageAtTrial});
                }
            },
            calculateDateOfTrial() {
                if (this.dateOfBirth && this.ageAtTrial) {
                    let dateOfBirth = moment(this.dateOfBirth, 'DD/MM/YYYY');
                    let ageAtTrial = this.ageAtTrial;
                    let dateOfTrial = dateOfBirth.add(ageAtTrial, 'years').format('DD/MM/YYYY');
                    this.updateValue({key: 'dateOfTrial', value: dateOfTrial});
                }
            },
            isValidAgeAtTrial() {
                if (!this.ogdenTableInfo || !this.ogdenTable) {
                    return true;
                }

                return (
                    this.ageAtTrial !== null
                    && this.ageAtTrial >= this.ogdenTableInfo.tables[this.ogdenTable].min
                    && this.ageAtTrial <= this.ogdenTableInfo.tables[this.ogdenTable].max
                );
            },
            ageAtTrialValidationMessage() {
                if (!this.ogdenTable) {
                    return '';
                }

                return 'The Age at Trial is required and must be between '
                    + this.ogdenTableInfo.tables[this.ogdenTable].min
                    + ' - '
                    + this.ogdenTableInfo.tables[this.ogdenTable].max;
            },
            isValidTerm() {
                if (!this.ogdenTableInfo || !this.ogdenTable) {
                    return true;
                }

                return (
                    this.term !== null
                    && this.term >= this.ogdenTableInfo.tables[this.ogdenTable].min
                    && this.term <= this.ogdenTableInfo.tables[this.ogdenTable].max
                );
            },
            termValidationMessage() {
                if (!this.ogdenTable) {
                    return '';
                }

                return 'The Term is required and must be between '
                    + this.ogdenTableInfo.tables[this.ogdenTable].min
                    + ' - '
                    + this.ogdenTableInfo.tables[this.ogdenTable].max;
            },
            formulaData() {
                let thead = this.tableData.table.thead;
                let tbody = this.tableData.table.tbody;
                let discountRate = this.discountRate+'%';
                let data = [];
                let columnCount = 0;

                _.forEach(thead, function(value, key) {
                    if (value === discountRate) {
                        columnCount = key;
                    }
                });

                _.forEach(tbody, function(value, key) {
                    data.push(value[0]);
                    data.push(value[columnCount]);
                });

                return data;
            },
            resetCheck() {
                if (this.findValue('ogdenTable') === 'table-28') {
                    this.updateValue({key: 'dateOfBirth', value: null});
                    this.updateValue({key: 'dateOfTrial', value: null});
                    this.updateValue({key: 'ageAtTrial', value: null});
                    this.updateValue({key: 'term', value: null});
                }
                this.updateValue({key: 'multiplier', value: 0});
                this.reCalculateMultiplier();
            }
        }
    }
</script>



// WEBPACK FOOTER //
// InterpolatedMultipliers.vue?62fe0215